// src/pages/Finance/OverduePayments/OverduePayments.jsx
import React, { useState, useEffect } from "react"
import { Row, Col, Spin, message, Button } from "antd"
import { WarningOutlined, HistoryOutlined } from "@ant-design/icons"
import { collection, query, where, onSnapshot } from "firebase/firestore"
import { db } from "../../../firebase/config"

import PageHeader from "../../../components/Elements/PageHeader"
import SelectUser from "./Overdue/SelectUser"
import UnpaidInvoicesTable from "./Overdue/UnpaidInvoicesTable"
import ClientInvoicesDrawer from "./Overdue/ClientInvoicesDrawer"

import dayjs from "dayjs"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { useLocation, useNavigate } from "react-router-dom"

const blockstyle = {
  background: "white",
  borderRadius: 15,
  padding: 25,
  width: "100%",
}

export default function OverduePayments() {
  const [selectedUser, setSelectedUser] = useState("all")
  const [docs, setDocs] = useState({ invoices: [], proformas: [], all: [] })
  const [loading, setLoading] = useState(true)

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedClientForDrawer, setSelectedClientForDrawer] = useState(null)

  const { user } = useAuthContext()
  const location = useLocation()
  const navigate = useNavigate()
  const isAdminRoute =
    location.pathname.startsWith("/admin") || location.pathname.startsWith("/dev-spec")

  const mergeDocs = (patch) =>
    setDocs((prev) => {
      const next = { ...prev, ...patch }

      const calcDaysLate = (doc) => {
        const due = dayjs(doc.payUntilFormatted, "DD.MM.YYYY")
        if (!due.isValid()) return 0
        const overdue = dayjs().startOf("day").diff(due, "day")
        return overdue > 0 ? overdue : 0
      }

      next.all = [...next.invoices, ...next.proformas]
        .filter((doc) => calcDaysLate(doc) > 0)
        .sort((a, b) => {
          const da = dayjs(a.payUntilFormatted, "DD.MM.YYYY")
          const db = dayjs(b.payUntilFormatted, "DD.MM.YYYY")
          if (!da.isValid()) return 1
          if (!db.isValid()) return -1
          return da.valueOf() - db.valueOf()
        })

      return next
    })

  const handleClientClick = (clientData) => {
    if (!clientData?.id) {
      console.warn("Invalid client data:", clientData)
      return
    }
    setSelectedClientForDrawer(clientData)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedClientForDrawer(null)
  }

  useEffect(() => {
    setLoading(true)

    const baseClauses = [
      // 0 = unpaid, 1 = unpaid with proforma, 3 = partially paid
      // 2 = fully paid → excluded
      where("paymentStatus", "in", [0, 1, 3]),
    ]

    const clauses = isAdminRoute
      ? selectedUser === "all"
        ? baseClauses
        : [...baseClauses, where("userId", "==", selectedUser)]
      : [...baseClauses, where("userId", "==", user.uid)]

    const makeQuery = (col) =>
      query(
        collection(db, col),
        ...clauses,
        where("docPrefix", "in", ["AR", "RX"]),
        where("deleted", "==", false),
      )

    const unsubInvoices = onSnapshot(
      makeQuery("invoices"),
      (snap) =>
        mergeDocs({
          invoices: snap.docs.map((d) => ({ id: d.id, docType: "invoice", ...d.data() })),
        }),
      (error) => {
        console.error("Error fetching invoices:", error)
        message.error("Failed to fetch invoices")
        setLoading(false)
      },
    )

    const unsubProformas = onSnapshot(
      makeQuery("proformas"),
      (snap) =>
        mergeDocs({
          proformas: snap.docs.map((d) => ({ id: d.id, docType: "proforma", ...d.data() })),
        }),
      (error) => {
        console.error("Error fetching proformas:", error)
      },
    )

    const timer = setTimeout(() => setLoading(false), 400)

    return () => {
      unsubInvoices()
      unsubProformas()
      clearTimeout(timer)
    }
  }, [selectedUser, user, isAdminRoute])

  if (loading) {
    return (
      <div
        style={{
          height: "calc(100vh - 200px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <PageHeader text="Overdue Payments" icon={<WarningOutlined />} />
            <Button icon={<HistoryOutlined />} onClick={() => navigate("/reminder-history")}>
              Reminder History
            </Button>
          </div>
        </Col>

        {isAdminRoute && (
          <Col span={24}>
            <div style={blockstyle}>
              <SelectUser selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
            </div>
          </Col>
        )}

        <Col span={24}>
          <div style={blockstyle}>
            <UnpaidInvoicesTable
              data={docs.all}
              loading={loading}
              onClientClick={handleClientClick}
            />
          </div>
        </Col>
      </Row>

      <ClientInvoicesDrawer
        visible={isDrawerOpen}
        onClose={handleDrawerClose}
        availableDocs={docs.all}
        clientData={selectedClientForDrawer}
      />
    </div>
  )
}
